import { BookOpen, Heart, Gamepad2, Compass, Camera, Telescope, Sparkles, Users } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1477093782505-e10aaeb27c6d?w=1200";

export default function About() {
  return (
    <div className="page-container fade-in" data-testid="about-page">
      <div className="page-eyebrow">the keeper's tale</div>
      <h1 className="page-title">About the Tavern</h1>
      <p className="page-sub">A few words about the soul running the lanterns at Nocturne Orchid.</p>

      <div className="parchment-card mt-10 p-8 md:p-12 grid md:grid-cols-[0.9fr_1.1fr] gap-10 items-center">
        <img src={ABOUT_IMG} alt="orchid" className="w-full max-h-[520px] object-cover rounded-sm" />
        <div>
          <div className="tavern-script text-3xl" style={{color:"var(--orchid-light)"}}>greetings, traveler</div>
          <h2 className="tavern-heading text-3xl md:text-4xl gold-text mt-2">I'm Nocturne Orchid.</h2>
          <p className="mt-5 leading-relaxed" style={{color:"var(--silver)"}}>
            Content creator, gamer, storyteller, and community builder. I spend my evenings exploring worlds
            (real and virtual), reading about the strange corners of history and science, taking photographs
            of places that don't technically exist, and building small, warm spaces online for kind, curious
            people to gather.
          </p>
          <p className="mt-4 leading-relaxed" style={{color:"var(--muted)"}}>
            Nocturne Orchid is the digital shape of all of that — a tavern where streams, stories, star charts,
            and slow conversations live under one mossy roof. It isn't a brand. It's a place to linger.
          </p>
        </div>
      </div>

      <div className="divider-ornament tavern-heading text-xl mt-16"><span>✦ What I Love ✦</span></div>

      <div className="bento-grid mt-8">
        {[
          {icon: Gamepad2, title: "Fallout & RPGs", text: "Wasteland radios and dialogue trees are my comfort genre. I love a long campaign, a well-written companion, and the kind of game that respects your time and your choices."},
          {icon: BookOpen, title: "History & Mysteries", text: "I'm pulled in by historical mysteries — forgotten libraries, vanished expeditions, the small footnotes that turn out to be the whole story. The past is the strangest thing humans have ever made."},
          {icon: Telescope, title: "Science & Astronomy", text: "Constellations, deep-sea biology, the way light behaves at the edges of things. Science delivered with a sense of wonder will always get my attention."},
          {icon: Users, title: "Community Building", text: "Welcoming is a skill. I care about making spaces where people feel safe enough to be curious out loud — Discord, streams, comment sections, all of it."},
          {icon: Camera, title: "Photography (real & virtual)", text: "I photograph the world I'm in, including the one I'm in via Second Life. A good photograph is a good photograph regardless of where its light comes from."},
          {icon: Sparkles, title: "Storytelling & Worldbuilding", text: "Folklore, microfiction, the lore of imaginary radio stations, characters who only exist on Sundays. I write to remember what mattered."},
          {icon: Compass, title: "Streaming & Creating", text: "Twitch is the campfire. YouTube is the archive. Creating is how I share the things I find, and streaming is how I get to share them in real time."},
          {icon: Heart, title: "Curiosity-Driven Learning", text: "I follow rabbit holes wherever they go — folklore, social issues, museum exhibits, half-remembered facts from childhood. Curiosity is the only compass I trust."},
        ].map((it, idx) => (
          <div key={idx} className="parchment-card p-6" data-testid={`about-card-${idx}`}>
            <span className="ornate-corner tl" /><span className="ornate-corner tr" />
            <span className="ornate-corner bl" /><span className="ornate-corner br" />
            <it.icon className="h-7 w-7" style={{color:"var(--gold)"}}/>
            <div className="tavern-heading text-xl mt-3 gold-text">{it.title}</div>
            <p className="text-sm mt-2" style={{color:"var(--muted)"}}>{it.text}</p>
          </div>
        ))}
      </div>

      <div className="parchment-card p-8 md:p-10 mt-10" data-testid="about-values">
        <div className="page-eyebrow">what this place stands for</div>
        <h3 className="tavern-heading text-2xl md:text-3xl gold-text mt-2">A few honest values</h3>
        <div className="grid sm:grid-cols-3 gap-5 mt-5 text-sm" style={{color:"var(--silver)"}}>
          <div><span className="gold-text tavern-heading text-lg">Kindness as a rule.</span> Curiosity, not cynicism. Make room for newcomers.</div>
          <div><span className="gold-text tavern-heading text-lg">Slow over loud.</span> A small, warm community will always be more interesting than a big, cold one.</div>
          <div><span className="gold-text tavern-heading text-lg">Inclusion matters.</span> Social issues aren't off-topic here. They're part of being a thoughtful human in the world.</div>
        </div>
        <p className="tavern-script text-xl mt-6" style={{color:"var(--orchid-light)"}}>
          the lanterns are lit for anyone who wants to read by them.
        </p>
      </div>
    </div>
  );
}
